/**
 * Authentication Context
 * 
 * Provides authentication state and methods throughout the app.
 * Uses Threedium App OAuth for login - users authenticate via Threedium,
 * then we exchange the app_user_id for our own JWT tokens.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authApi, userApi, setAuthToken, healthApi } from '../services/cloudApi';
import { getAuthToken, isTokenExpiringSoon } from '../utils/auth';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [cloudAvailable, setCloudAvailable] = useState(false);
    const [error, setError] = useState(null);
    const [sessionExpiringSoon, setSessionExpiringSoon] = useState(false);

    // Pre-logout callbacks — run BEFORE the token is cleared so downstream
    // cleanup (e.g. ending the ECS session) can still authenticate.
    const preLogoutCallbacksRef = useRef([]);

    // Check if cloud backend is available
    const checkCloudHealth = useCallback(async () => {
        try {
            const available = await healthApi.check();
            setCloudAvailable(available);
            return available;
        } catch {
            setCloudAvailable(false);
            return false;
        }
    }, []);

    // Load user from token on mount
    const loadUser = useCallback(async () => {
        setIsLoading(true);
        try {
            // First check if cloud is available
            const available = await checkCloudHealth();
            if (!available) {
                setIsLoading(false);
                return;
            }

            // Check if we have a token
            if (!authApi.isAuthenticated()) {
                setIsLoading(false);
                return;
            }

            // Load user profile
            const userData = await authApi.me();
            setUser(userData);
            setIsAuthenticated(true);
        } catch (err) {
            console.error('Failed to load user:', err);
            setAuthToken(null);
            setUser(null);
            setIsAuthenticated(false);
        } finally {
            setIsLoading(false);
        }
    }, [checkCloudHealth]);

    useEffect(() => {
        loadUser();

        // Listen for unauthorized events
        const handleUnauthorized = () => {
            setUser(null);
            setIsAuthenticated(false);
            setSessionExpiringSoon(false);
        };
        window.addEventListener('auth:unauthorized', handleUnauthorized);
        return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
    }, [loadUser]);

    // Check session expiry warning periodically (every minute)
    useEffect(() => {
        if (!isAuthenticated) {
            setSessionExpiringSoon(false);
            return;
        }
        const checkExpiry = () => setSessionExpiringSoon(isTokenExpiringSoon(getAuthToken()));
        checkExpiry();
        const interval = setInterval(checkExpiry, 60 * 1000);
        return () => clearInterval(interval);
    }, [isAuthenticated]);

    // Login via Threedium OAuth - redirect to Threedium login page
    const loginWithThreedium = async () => {
        setError(null);
        try {
            const { login_url } = await authApi.getLoginUrl();
            window.location.href = login_url;
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        }
    };

    // Handle Threedium OAuth callback (called from callback page)
    const handleThreediumCallback = async (userAppId, otp) => {
        setError(null);
        try {
            const response = await authApi.threediumCallback(userAppId, otp);
            if (response.access_token) {
                localStorage.setItem('refresh_token', response.refresh_token);
                await loadUser();
                return { success: true };
            }
            return { success: false, error: 'No access token received' };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        }
    };

    /**
     * Register a callback to run BEFORE logout clears the auth token.
     * Returns an unregister function.
     */
    const registerPreLogoutCallback = useCallback((cb) => {
        preLogoutCallbacksRef.current.push(cb);
        return () => {
            preLogoutCallbacksRef.current = preLogoutCallbacksRef.current.filter(c => c !== cb);
        };
    }, []);

    // Logout — runs all pre-logout hooks while the token is still valid,
    // then clears credentials.
    const logout = async () => {
        // Run pre-logout hooks (e.g. end ECS session) while token is still valid
        for (const cb of preLogoutCallbacksRef.current) {
            try { await cb(); } catch (e) { console.error('Pre-logout hook error:', e); }
        }

        try {
            const refreshToken = localStorage.getItem('refresh_token');
            if (refreshToken) {
                await authApi.logout(refreshToken);
            }
        } catch (err) {
            console.error('Logout error:', err);
        } finally {
            setAuthToken(null);
            localStorage.removeItem('refresh_token');
            setUser(null);
            setIsAuthenticated(false);
        }
    };

    // Update user profile
    const updateProfile = async (data) => {
        try {
            const updated = await userApi.updateProfile(data);
            setUser(prev => ({ ...prev, ...updated }));
            return { success: true };
        } catch (err) {
            return { success: false, error: err.message };
        }
    };

    // Check if user is admin (API returns uppercase roles)
    const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPERADMIN';
    const isSuperAdmin = user?.role === 'SUPERADMIN';

    const value = {
        user,
        isLoading,
        isAuthenticated,
        cloudAvailable,
        error,
        sessionExpiringSoon,
        isAdmin,
        isSuperAdmin,
        loginWithThreedium,
        handleThreediumCallback,
        logout,
        registerPreLogoutCallback,
        updateProfile,
        refreshUser: loadUser,
        clearError: () => setError(null),
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export default AuthContext;
